import { fmtDayLong } from '@concord/domain';
import type { IntakeView } from '@concord/domain/contracts';
import { Notice, SummaryPanel, WizardLayout, WizardProgress } from '@concord/ui';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { errorMessage } from '../../lib/api';
import { useIntake, usePatchIntake } from './api';
import { isStepDone, STEPS, stepByKey, summaryValue } from './steps';
import { AboutStep } from './steps/AboutStep';
import { CaseTypeStep } from './steps/CaseTypeStep';
import { HistoryStep } from './steps/HistoryStep';
import { PoolStep } from './steps/PoolStep';
import { RecordsStep } from './steps/RecordsStep';
import { ReviewStep } from './steps/ReviewStep';
import { TimesStep } from './steps/TimesStep';

export interface StepProps {
  v: IntakeView;
  /** Moves to another step, saving the furthest step reached. */
  go: (key: string) => void;
  back: () => void;
  next: () => void;
  /** Who is filling it in: the patient, or a navigator on their behalf (INT-12). */
  mode: 'patient' | 'staff';
  onSent: (reference: string) => void;
}

/**
 * The conversational intake (INT-01, INT-02): one decision per screen, the running
 * summary always visible and every answer changeable from it.
 */
export function IntakeWizard({
  mode,
  base,
  onSent,
  frame,
}: {
  mode: 'patient' | 'staff';
  /** Route prefix, e.g. "/cases/<id>/intake". */
  base: string;
  onSent: (reference: string) => void;
  frame: (content: ReactNode, v: IntakeView | undefined) => ReactNode;
}) {
  const { id = '', step } = useParams();
  const navigate = useNavigate();
  const q = useIntake(id);
  const patch = usePatchIntake(id);
  const current = stepByKey(step);

  if (q.isPending) return frame(<div className="skeleton" style={{ height: 320 }} />, undefined);
  if (q.isError) return frame(<Notice tone="bad">{errorMessage(q.error)}</Notice>, undefined);
  const v = q.data;
  if (v.state !== 'draft') {
    return frame(
      <Notice>
        This case was sent{v.reference ? ` as ${v.reference}` : ''}. It can no longer be changed here.
      </Notice>,
      v,
    );
  }

  const go = (key: string) => {
    const target = stepByKey(key);
    if (target.n > v.intakeStep) patch.mutate({ intakeStep: target.n });
    navigate(`${base}/${target.key}`);
    window.scrollTo(0, 0);
  };
  const idx = STEPS.indexOf(current);
  const props: StepProps = {
    v,
    go,
    back: () => go(STEPS[Math.max(0, idx - 1)]!.key),
    next: () => go(STEPS[Math.min(STEPS.length - 1, idx + 1)]!.key),
    mode,
    onSent,
  };
  const nextStep = STEPS[idx + 1];
  const started = new Date(v.startedAt);
  const tz = v.patient.timezone;

  const body = (
    <WizardLayout
      summary={
        <SummaryPanel
          title={mode === 'staff' ? `${v.patient.firstName}'s case so far` : 'Your case so far'}
          rows={STEPS.map((s) => {
            const done = s.key !== current.key && isStepDone(v, s.key);
            const { value, sub, soft } = summaryValue(v, s.key);
            return {
              key: s.key,
              label: s.label,
              value: sub ? (
                <>
                  {value} <span className="sub2">{sub}</span>
                </>
              ) : (
                value
              ),
              soft: soft && s.key !== current.key,
              state: s.key === current.key ? 'now' : done ? 'done' : 'todo',
              onChange: () => go(s.key),
            };
          })}
          foot={`Started ${fmtDayLong(started, tz)}${v.device ? ` on ${v.device === 'phone' ? 'your phone' : `a ${v.device}`}` : ''}${
            mode === 'staff' ? ' by phone with a navigator' : ''
          }. Nothing is sent to a physician until ${mode === 'staff' ? 'the case is reviewed and confirmed' : 'you review and confirm'}.`}
        />
      }
    >
      <WizardProgress
        step={current.n}
        total={STEPS.length}
        label={current.title}
        next={nextStep ? nextStep.short : null}
      />
      {current.key === 'type' && <CaseTypeStep {...props} />}
      {current.key === 'panel' && <PoolStep {...props} />}
      {current.key === 'about' && <AboutStep {...props} />}
      {current.key === 'history' && <HistoryStep {...props} />}
      {current.key === 'records' && <RecordsStep {...props} />}
      {current.key === 'times' && <TimesStep {...props} />}
      {current.key === 'review' && <ReviewStep {...props} />}
    </WizardLayout>
  );
  return frame(body, v);
}
